import { useRef, useState } from 'react';

interface MpinInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Number of digit boxes (MPIN is 4–6 digits; default 6). */
  length?: number;
  /** Mask each digit (show a dot). Default true. */
  mask?: boolean;
  /** Show the reveal (eye) toggle when masked. Default true. */
  showToggle?: boolean;
  id?: string;
  autoFocus?: boolean;
  invalid?: boolean;
  testId?: string;
}

/**
 * Segmented MPIN input — one box per digit. Not an OTP widget; purpose-built for a
 * persistent numeric MPIN (auto-advance, backspace, paste, arrow-key navigation).
 * Value is the concatenation of the entered digits, so 4–6 digit MPINs all work
 * (shorter MPINs simply leave trailing boxes empty).
 */
export const MpinInput = ({
  value,
  onChange,
  length = 6,
  mask = true,
  showToggle = true,
  id,
  autoFocus,
  invalid,
  testId,
}: MpinInputProps) => {
  const refs = useRef<(HTMLInputElement | null)[]>([]);
  // Masked by default; the eye toggle reveals the digits (standard password affordance).
  const [reveal, setReveal] = useState(false);
  const digits = Array.from({ length }, (_, i) => value[i] ?? '');
  const hidden = mask && !reveal;

  const focusBox = (i: number) => {
    const el = refs.current[Math.max(0, Math.min(length - 1, i))];
    el?.focus();
    el?.select();
  };

  const setDigit = (i: number, d: string) => {
    const arr = digits.slice();
    arr[i] = d;
    onChange(arr.join(''));
  };

  const handleChange = (i: number, raw: string) => {
    const d = raw.replace(/\D/g, '').slice(-1); // keep only the last typed digit
    setDigit(i, d);
    if (d && i < length - 1) focusBox(i + 1);
  };

  const handleKeyDown = (i: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) {
        setDigit(i, '');
      } else if (i > 0) {
        setDigit(i - 1, '');
        focusBox(i - 1);
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      focusBox(i - 1);
    } else if (e.key === 'ArrowRight' && i < length - 1) {
      focusBox(i + 1);
    }
  };

  const handlePaste = (i: number, e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasted = (e.clipboardData.getData('text') || '').replace(/\D/g, '').slice(0, length);
    if (!pasted) return;
    const arr = digits.slice();
    for (let k = 0; k < pasted.length && i + k < length; k++) arr[i + k] = pasted[k];
    onChange(arr.join(''));
    focusBox(Math.min(i + pasted.length, length - 1));
  };

  return (
    <div className="flex items-center gap-2 w-full" role="group" aria-label="MPIN" data-testid={testId}>
      {/* Hide the browser's native password-reveal / clear controls (Edge's
          ::-ms-reveal / ::-ms-clear) so only our single eye toggle shows. */}
      <style>{`.mpin-box::-ms-reveal, .mpin-box::-ms-clear { display: none; }`}</style>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => { refs.current[i] = el; }}
          id={i === 0 ? id : undefined}
          type={hidden ? 'password' : 'text'}
          inputMode="numeric"
          autoComplete="off"
          maxLength={1}
          value={d}
          autoFocus={!!autoFocus && i === 0}
          onChange={(e) => handleChange(i, e.target.value)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          onPaste={(e) => handlePaste(i, e)}
          onFocus={(e) => e.target.select()}
          className={`mpin-box flex-1 min-w-0 h-12 text-center text-lg rounded-lg border bg-gray-50 dark:bg-gray-800 text-ink transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
            invalid ? 'border-red-500' : 'border-gray-300 dark:border-gray-600'
          }`}
        />
      ))}
      {mask && showToggle && (
        <button
          type="button"
          onClick={() => setReveal((v) => !v)}
          aria-label={reveal ? 'Hide MPIN' : 'Show MPIN'}
          aria-pressed={reveal}
          title={reveal ? 'Hide MPIN' : 'Show MPIN'}
          tabIndex={-1}
          className="shrink-0 h-12 ml-1 pl-2 flex items-center justify-center bg-transparent border-none text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 transition-colors cursor-pointer focus:outline-none"
          data-testid={testId ? `${testId}.Toggle` : undefined}
        >
          <i className={reveal ? 'pi pi-eye-slash' : 'pi pi-eye'} style={{ fontSize: '1rem' }} />
        </button>
      )}
    </div>
  );
};

export default MpinInput;
